import './landing-entry.css'
import type { Locale } from './i18n'
import { readAnalyticsConsent } from './lib/analyticsConsentState'

const ENHANCEMENT_IDLE_DELAY_MS = 4_000
const LOCALES = new Set<Locale>(['en', 'fr', 'nl', 'de'])

let enhancementPromise: Promise<void> | null = null

function localeFromDocument(): Locale | null {
  const value = document.querySelector<HTMLElement>('.landing-page[data-landing-locale]')?.dataset.landingLocale
  return value && LOCALES.has(value as Locale) ? value as Locale : null
}

function initializeLandingClient(): Promise<void> {
  enhancementPromise ??= Promise.all([
    import('./lib/audiencePreferences'),
    import('./landing/index'),
    import('./lib/analytics'),
    import('./lib/documentAnalytics'),
    import('./lib/publicRoute'),
    import('./lib/staticDocument'),
    import('./components/LandingNewsletter'),
    import('./components/ConsentManager'),
    import('react'),
    import('react-dom/client'),
  ]).then(([
    ,
    { initializeLandingExperience },
    { captureInitialCampaign },
    { documentAnalyticsStartup },
    { readPublicRoute },
    { initializeDocumentAnalytics },
    { LandingNewsletter },
    { ConsentManager },
    { StrictMode, createElement },
    { createRoot },
  ]) => {
    captureInitialCampaign(window.location.search)
    const route = readPublicRoute()
    const startup = documentAnalyticsStartup(route, window.location, false)
    if (startup.action !== 'initialize') return

    initializeDocumentAnalytics(route, startup.pageType, startup.trackPageViewImmediately)
    initializeLandingExperience()

    const consentRoot = document.querySelector<HTMLElement>('[data-landing-consent]')
    const newsletterRoot = document.querySelector<HTMLElement>('[data-landing-newsletter]')
    const locale = route?.locale ?? localeFromDocument()
    if (newsletterRoot && locale) {
      createRoot(newsletterRoot).render(createElement(StrictMode, null, createElement(LandingNewsletter, { locale })))
    }
    if (consentRoot && locale) {
      createRoot(consentRoot).render(createElement(StrictMode, null, createElement(ConsentManager, { locale })))
    }
  })
  return enhancementPromise
}

function scheduleLandingClient(): void {
  let timer: number | null = null
  let started = false
  const intentEvents: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'scroll']

  const cleanup = () => {
    if (timer !== null) window.clearTimeout(timer)
    window.removeEventListener('load', scheduleIdle)
    for (const event of intentEvents) window.removeEventListener(event, start, true)
  }
  const start = () => {
    if (started) return
    started = true
    cleanup()
    void initializeLandingClient()
  }
  const scheduleIdle = () => {
    timer = window.setTimeout(start, ENHANCEMENT_IDLE_DELAY_MS)
  }

  for (const event of intentEvents) window.addEventListener(event, start, { capture: true, passive: true, once: true })
  if (document.readyState === 'complete') scheduleIdle()
  else window.addEventListener('load', scheduleIdle, { once: true })
}

const hasPreferencePayload = window.location.hash.includes('preferences=')
  || new URLSearchParams(window.location.search).has('subscription')
const hasNewsletterAnchor = window.location.hash === '#updates'
const hasStoredAnalyticsGrant = readAnalyticsConsent()?.decision === 'granted'

if (hasPreferencePayload || hasNewsletterAnchor || hasStoredAnalyticsGrant) void initializeLandingClient()
else scheduleLandingClient()
